import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { Alert } from "antd";
import ProtectedRoute from "./auth/ProtectedRoute.jsx";
import { getData } from "./config/api-connect";
import { LeaveProvider, LeaveContext } from "./contexts/LeaveContext";
import { PermissionProvider } from "./contexts/PermissionContext";
import Login from "./pages/login";
import NotFound from "./pages/pageNotFound";
import Spinner from "./components/spinner";

// Only ever rendered post-login — code-split out of the pre-login bundle so
// an unauthenticated visitor never downloads them.
const SideBar = lazy(() => import("./components/sideBar"));
const AddContract = lazy(() => import("./pages/contract/addContract"));
const ContractInfo = lazy(() => import("./pages/contract/contract"));
const Overview = lazy(() => import("./pages/overview"));
const Revenue = lazy(() => import("./pages/revenue"));
const Collection = lazy(() => import("./pages/collection"));
const AddExpense = lazy(() => import("./pages/expense"));
const AddBuddyData = lazy(() => import("./pages/buddyData"));
const SalaryDetails = lazy(() => import("./pages/salaryDetails"));
const BuddyDetails = lazy(() => import("./pages/buddyInfo"));
const ExpenseDetails = lazy(() => import("./pages/expenseDetail"));
const ContractTermAddition = lazy(() => import("./pages/contract/contractTerm"));
const Dashboard = lazy(() => import("./pages/dashboard"));
const Batches = lazy(() => import("./pages/batches"));
const Coaches = lazy(() => import("./pages/coaches/coaches"));
const Attendance = lazy(() => import("./pages/batches/attendance.jsx"));
const Leads = lazy(() => import("./pages/leads"));
const Reports = lazy(() => import("./pages/reports"));
const CollectionList = lazy(() => import("./pages/collectionList"));
const BatchDetails = lazy(() => import("./pages/batchDetails"));
const AttendanceSummary = lazy(() => import("./pages/batchDetails/attendanceSummary"));
const RevenueDetails = lazy(() => import("./pages/customerList/revenueDetails"));
const CollectionDetails = lazy(() => import("./pages/customerList/collectionDetails"));
const LeadList = lazy(() => import("./pages/customerList/leadList"));
const Customers = lazy(() => import("./pages/customers"));
const RenewalAnalysis = lazy(() => import("./pages/customers/renewalAnalysis"));
const RenewalDrilldown = lazy(() => import("./pages/customers/renewalDrilldown"));
const CustomerFeedback = lazy(() => import("./pages/customers/customerFeedback"));
const RetentionAnalysis = lazy(() => import("./pages/customers/retentionAnalysis"));
const NewCustomers = lazy(() => import("./pages/customers/newCustomers"));
const CustomerSummary = lazy(() => import("./pages/customers/customerSummary"));
const CustomerList = lazy(() => import("./pages/customerList/customerList"));
const UnverifiedCollections = lazy(() => import("./pages/collectionList/unverifiedCollections"));
const TotalCollections = lazy(() => import("./pages/collectionList/totalCollections"));
const CollectionSummary = lazy(() => import("./pages/collectionList/collectionSummary"));
const PipelineDrilldown = lazy(() => import("./pages/leads/pipelineDrilldown"));
const LeadsProcessed = lazy(() => import("./pages/leads/leadsProcessed"));
const LeadsDetails = lazy(() => import("./pages/leads/leadsList"));
const RevenueSummary = lazy(() => import("./pages/revenue/revenueSummary"));
const CollectionListSummary = lazy(() => import("./pages/collectionList/collectionListSummary"));
const Careers = lazy(() => import("./pages/careers/jobListing.jsx"));
const ApplicantList = lazy(() => import("./pages/careers/applicantList.jsx"));
const AddJob = lazy(() => import("./pages/careers/addJob.jsx"));
const EditJob = lazy(() => import("./pages/careers/editJob.jsx"));
const Leaves = lazy(() => import("./pages/leaves/leaveManagement.jsx"));
const LeaveApplications = lazy(() => import("./pages/leaves/leaveApplications.jsx"));
const IDCards = lazy(() => import("./pages/visitors"));
const VisitorList = lazy(() => import("./pages/visitors/visitorList"));
const BuddyAttendance = lazy(() => import("./pages/attendance/buddyAttendance"));
const StaffManagement = lazy(() => import("./pages/attendance/staffManagement"));
const Reimbursement = lazy(() => import("./pages/reimbursement"));

function App() {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeaveAlert, setShowLeaveAlert] = useState(true);

  function LeaveAlert({ visible, onClose }) {
    const navigate = useNavigate();
    const { pendingLeaveCount } = React.useContext(LeaveContext) || { pendingLeaveCount: 0 };
    if (!visible || !pendingLeaveCount) return null;
    
    const handleAlertClick = (e) => {
      // Don't navigate if close button is clicked
      if (e.target.closest('.ant-alert-close-icon')) return;
      navigate('/leaveApplications');
      onClose();
    };
    
    return (
      <AlertContainer onClick={handleAlertClick} style={{ cursor: 'pointer' }}>
        <Alert
          // message="Leave request pending"
          description={
            pendingLeaveCount === 1
              ? "A team member has applied for leave. Please review it."
              : `${pendingLeaveCount} team members have applied for leave. Please review them.`
          }
          type="warning"
          closable
          onClose={onClose}
          style={{background:'#fffbe6',border: '1px solid #ffe58f',color: '#d48806',borderRadius:5}}
        />
      </AlertContainer>
    );
  }

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await getData("/auth/auth_status");
        const isLoggedIn = response.success && response.logged_in;
        setIsUserLoggedIn(isLoggedIn);
        
        if (isLoggedIn) {
          localStorage.setItem('access_id', response.user.id);
          localStorage.setItem('user_role', response.user.role);
          localStorage.setItem('user_name', response.user.name);
        }
      } catch (error) {
        console.log("User not logged in:", error);
        setIsUserLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    checkLoginStatus();
  }, []);

  if (loading) {
    return <MainContainer><LoadingContainer><Spinner /></LoadingContainer></MainContainer>;
  }

  return (
    <Router>
      <LeaveProvider>
      <MainContainer>
        {isUserLoggedIn ? (
          <PermissionProvider>
            <Suspense fallback={<Spinner />}>
              <SideBar />
            </Suspense>
            <ContainerBody>
              <LeaveAlert visible={showLeaveAlert} onClose={() => setShowLeaveAlert(false)} />
              <Suspense fallback={<Spinner />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/revenue" element={<Revenue />} />
                <Route
                  path="/collection"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionList />
                    </ProtectedRoute>
                  }
                />
                <Route path="/addContract" element={<AddContract />} />
                <Route path="/contractInfo" element={<ContractInfo />} />
                <Route path="/addExpense" element={<AddExpense />} />
                <Route
                  path="/addcontractterms"
                  element={<ContractTermAddition />}
                />
                <Route path="/salaryDetails" element={<SalaryDetails />} />
                <Route path="/buddyData" element={<AddBuddyData />} />
                <Route path="/buddyinfo" element={<BuddyDetails />} />
                <Route path="/expenseDetails" element={<ExpenseDetails />} />
                <Route
                  path="/batches/attendance-summary/:venue_id/:game_level"
                  element={<AttendanceSummary />}
                />
                <Route
                  path="/batches/utilization/:venue_id/:game_level"
                  element={<BatchDetails />}
                />
                <Route
                  path="/batches/attendance/:venue_id/:game_level"
                  element={<BatchDetails />}
                />
                <Route path="/batches/utilization" element={<Batches />} />
                <Route path="/batches/attendance" element={<Attendance />} />
                <Route path="/coaches" element={<Coaches />} />
                <Route
                  path="/reimbursement"
                  element={
                    <ProtectedRoute permission="access_finance_reimbursements">
                      <Reimbursement />
                    </ProtectedRoute>
                  }
                />
                <Route path="/lead-pipeline" element={<Leads />} />
                <Route path="/hot-leads" element={<Leads />} />
                <Route path="/leads-generated" element={<Leads />} />
                <Route path="/leads-processed" element={<Leads />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/customers" element={<Customers />} />
                <Route
                  path="/batchDetails/:venue_id/:game_level"
                  element={<BatchDetails />}
                />
                <Route
                  path="/revenueList/:venue_id"
                  element={<RevenueDetails />}
                />
                <Route
                  path="/collectionList/:venue_id"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionDetails />
                    </ProtectedRoute>
                  }
                />
                <Route path="/leadList/:venue_id" element={<LeadList />} />
                <Route
                  path="/customers/:venue_id"
                  element={<CustomerSummary />}
                />
                <Route
                  path="/renewal-analysis/"
                  element={<RenewalAnalysis />}
                />
                <Route
                  path="/retention-analysis/"
                  element={<RetentionAnalysis />}
                />
                <Route
                  path="/renewal-analysis/:type/:monthYear"
                  element={<RenewalDrilldown />}
                />
                <Route
                  path="/customer-feedback"
                  element={<CustomerFeedback />}
                />
                <Route
                  path="/retention-analysis/new/:monthYear"
                  element={<NewCustomers />}
                />
                <Route
                  path="/collection-summary/:venue_id/:venue_name"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/collections/unverified"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <UnverifiedCollections />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/collections/total"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <TotalCollections />
                    </ProtectedRoute>
                  }
                />
                <Route path="/customerList" element={<CustomerList />} />
                <Route path="/leadsProcessed/" element={<LeadsProcessed />} />
                <Route
                  path="/lead-pipeline/details"
                  element={<PipelineDrilldown />}
                />
                <Route path="/leadDetails/" element={<LeadsDetails />} />
                <Route
                  path="/revenue/:venue_id/:type"
                  element={<RevenueSummary />}
                />
                <Route
                  path="/collection/:venue_id/:type"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionListSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/careers"
                  element={
                    <ProtectedRoute
                      roles={[
                        "HR",
                        "Delivery Manager",
                        "Trespasser",
                        "Lead Buddy",
                        "Marketing Manager"
                      ]}
                    >
                      <Careers />
                    </ProtectedRoute>
                  }
                />
                <Route path="/careers/:jobId" element={<ApplicantList />} />
                <Route path="/careers/add" element={<AddJob />} />
                <Route path="/careers/edit/:id" element={<EditJob />} />
                <Route path="/leaves" element={<Leaves />} />
                <Route
                  path="/leaveApplications"
                  element={<LeaveApplications />}
                />
                <Route path="/visitors/id-cards" element={<IDCards />} />
                <Route path="/visitors/list" element={<VisitorList />} />
                <Route
                  path="/hr/staff-attendance"
                  element={<BuddyAttendance />}
                />
                <Route
                  path="/hr/staff-management"
                  element={
                    <ProtectedRoute
                      roles={["HR", "Delivery Manager", "Trespasser"]}
                    >
                      <StaffManagement />
                    </ProtectedRoute>
                  }
                />
                {/* NotFound route moved outside the conditional rendering */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ContainerBody>
          </PermissionProvider>
        ) : (
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        )}
      </MainContainer>
      </LeaveProvider>
    </Router>
  );
}

export default App;

export const MainContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const ContainerBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

export const AlertContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 16px;
  width: 320px;
  z-index: 1100;
  pointer-events: auto;
`;
